import { removeLocalStorage } from '@/utils/common/storage'
import { goPage } from '@/utils/browser/page'

import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/stores/user.stores'
import { useMessage } from '@/composables/useMessage'
import { useToken } from '@/composables/useToken'
import { useFetchUser } from '@/composables/useFetchUser'
import { getI18nTranslate } from '@/utils/i18nUtils'
import { STORE_INFO, STORAGE_USER } from '@/constants/storageKey.const'

/**
 * 路由認證守衛，在每次路由切換前驗證使用者的 JWT token 狀態。
 *
 * 四段分支邏輯（依序判斷）：
 * 1. 無效 token + 目標非登入頁 → 清除狀態，顯示未登入提示，導向登入頁
 * 2. 有效 token + 目標非登入頁 → 補取使用者資訊（若尚未載入），放行
 * 3. 有效 token + 目標為登入頁 → 已登入不需再登，直接導向首頁
 * 4. token 存在但解析失敗（格式錯誤等）→ 清除狀態，顯示過期提示，導向登入頁
 */
export const authMiddleware = async ({
  to,
  from,
  next
}: {
  to: RouteLocationNormalized
  from: RouteLocationNormalized
  next: NavigationGuardNext
}) => {
  const router = useRouter()
  const userStore = useUserStore()
  const token = userStore.token
  const { openMessage } = useMessage()
  const { checkTokenValidity } = useToken()
  const { fnGetUserInfo } = useFetchUser()
  const tokenValid = checkTokenValidity(token)

  // 分支 1：無效 token 且目標頁面需要認證 → 清除狀態並導向登入
  if (to.name !== 'Login' && !tokenValid) {
    userStore.initLoginState()
    removeLocalStorage(STORAGE_USER, true)
    removeLocalStorage(STORE_INFO)
    return openMessage('error', getI18nTranslate('Common.Result.NotLogin'), { duration: 2 }, () => {
      goPage('/login')
    })
  }

  // 分支 2：有效 token 且目標頁面需要認證 → 確保 userInfo 已載入後放行
  if (to.name !== 'Login' && tokenValid) {
    const { name } = userStore.userInfo
    // 需等待 userInfo 回應：其中的 webVersion/apiVersion 會決定 systemVersionStore
    // 的降級檢查結果，之後頁面（如首頁）取資料時要拿到的是檢查後的值
    // Pinia storage plugin 已從 localStorage 還原 userInfo，此處僅補取最新資料
    // catchErrorHandler 內部會 re-throw，若不 catch，router 的 middleware runner
    // 沒有處理 rejected promise，會讓 next() 永遠不被呼叫、導航卡死
    if (!name) {
      try {
        await fnGetUserInfo(token)
      } catch {
        // 取得失敗不阻擋導航，systemVersion 沿用既有（persist）的值
      }
    }
    return next()
  }

  // 分支 3：有效 token 且目標為登入頁 → 已登入，跳過登入頁直接導向首頁
  if (to.name === 'Login' && tokenValid) {
    router.push({ name: 'Home' })
    return
  }

  // 分支 4：token 存在但驗證失敗（可能已過期或格式損毀）→ 清除並提示重新登入
  if (token && !checkTokenValidity(token)) {
    userStore.initLoginState()
    removeLocalStorage(STORAGE_USER, true)
    removeLocalStorage(STORE_INFO)
    openMessage('warning', getI18nTranslate('Common.Result.TokenExpired'), {}, () => {
      goPage('/login')
    })
    return
  }

  next()
}
